/**
 * Sidebar Component
 *
 * Role-aware navigation sidebar for the authenticated layout.
 * Shows different navigation links based on user role.
 * Collapsible on all screen sizes with sticky positioning.
 */

import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { billingApi } from '../../services/billingApi';
import type { Role } from '../../../../shared/types';

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  roles: Role[];
}

/**
 * Navigation items configuration
 * Each item specifies which roles can see it
 */
const NAV_ITEMS: NavItem[] = [
  {
    path: '/admin',
    label: 'Dashboard',
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="3" y="3" width="7" height="9" />
        <rect x="14" y="3" width="7" height="5" />
        <rect x="14" y="12" width="7" height="9" />
        <rect x="3" y="16" width="7" height="5" />
      </svg>
    ),
    roles: ['SUPER_ADMIN', 'ADMIN'],
  },
  {
    path: '/manager',
    label: 'Dashboard',
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="3" y="3" width="7" height="9" />
        <rect x="14" y="3" width="7" height="5" />
        <rect x="14" y="12" width="7" height="9" />
        <rect x="3" y="16" width="7" height="5" />
      </svg>
    ),
    roles: ['COMPANY_MANAGER'],
  },
  {
    path: '/dashboard',
    label: 'Dashboard',
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="3" y="3" width="7" height="9" />
        <rect x="14" y="3" width="7" height="5" />
        <rect x="14" y="12" width="7" height="9" />
        <rect x="3" y="16" width="7" height="5" />
      </svg>
    ),
    roles: ['COMPANY_USER'],
  },
  {
    path: '/admin/companies',
    label: 'Companies',
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </svg>
    ),
    roles: ['SUPER_ADMIN', 'ADMIN'],
  },
  {
    path: '/admin/users',
    label: 'Users',
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
    roles: ['SUPER_ADMIN', 'ADMIN'],
  },
  {
    path: '/admin/generations',
    label: 'Generations',
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polygon points="12 2 2 7 12 12 22 7 12 2" />
        <polyline points="2 17 12 22 22 17" />
        <polyline points="2 12 12 17 22 12" />
      </svg>
    ),
    roles: ['SUPER_ADMIN', 'ADMIN'],
  },
  {
    path: '/admin/bundles',
    label: 'Bundles',
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
        <line x1="7" y1="7" x2="7.01" y2="7" />
      </svg>
    ),
    roles: ['SUPER_ADMIN', 'ADMIN'],
  },
  {
    path: '/admin/invoices',
    label: 'Invoices',
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10 9 9 9 8 9" />
      </svg>
    ),
    roles: ['SUPER_ADMIN', 'ADMIN'],
  },
  {
    path: '/admin/settings',
    label: 'Settings',
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
      </svg>
    ),
    roles: ['SUPER_ADMIN'],
  },
  {
    path: '/manager/generations',
    label: 'Generations',
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polygon points="12 2 2 7 12 12 22 7 12 2" />
        <polyline points="2 17 12 22 22 17" />
        <polyline points="2 12 12 17 22 12" />
      </svg>
    ),
    roles: ['COMPANY_MANAGER'],
  },
  {
    path: '/manager/transactions',
    label: 'Transactions',
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <line x1="12" y1="1" x2="12" y2="23" />
        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
      </svg>
    ),
    roles: ['COMPANY_MANAGER'],
  },
  {
    path: '/manager/invoices',
    label: 'Deposit History',
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10 9 9 9 8 9" />
      </svg>
    ),
    roles: ['COMPANY_MANAGER'],
  },
  {
    path: '/manager/api-keys',
    label: 'API Keys',
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
      </svg>
    ),
    roles: ['SUPER_ADMIN', 'ADMIN', 'COMPANY_MANAGER'],
  },
  {
    path: '/dashboard/generations',
    label: 'My Generations',
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polygon points="12 2 2 7 12 12 22 7 12 2" />
        <polyline points="2 17 12 22 22 17" />
        <polyline points="2 12 12 17 22 12" />
      </svg>
    ),
    roles: ['COMPANY_USER'],
  },
];

/**
 * Editorial grouping for nav items. Items land in one of two sections —
 * "workspace" (the work the user is here to do) or "team" (billing +
 * account). Derived from path/label so we don't have to tag every
 * NavItem by hand.
 */
function sidebarGroup(path: string, label: string): 'workspace' | 'team' {
  const s = `${path} ${label}`.toLowerCase();
  if (/transaction|invoice|bundle|deposit|setting|billing/.test(s)) return 'team';
  return 'workspace';
}

const ROLE_LABELS: Record<Role, string> = {
  SUPER_ADMIN: 'Super Admin',
  ADMIN: 'Admin',
  READONLY_ADMIN: 'Read-Only Admin',
  COMPANY_MANAGER: 'Company Manager',
  COMPANY_USER: 'Company User',
};

/**
 * Short stable build tag the editorial brand subtitle expects (e.g.
 * "v2.0 · #a3f7c2"). Derived once per bundle load so every sidebar
 * mount in the same session shows the same tag without churning.
 */
const BUILD_TAG = `#${Math.random().toString(36).slice(2, 8)}`;

/**
 * Friendly display name + 2-char initials derived from the user's
 * email. Names like `smoke-test@company.com` → "Smoke Test" / "SM",
 * `manager@company.com` → "Manager" / "MA". Fallback to the full
 * email when the derived name would collide with the role label
 * (avoids the "Admin / Admin" duplicate).
 */
function deriveDisplayName(
  email: string,
  roleLabel: string,
): { name: string; initials: string } {
  const local = email.split('@')[0] ?? '';
  const parts = local.split(/[-._]+/).filter(Boolean);
  const derived = parts
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
    .join(' ');
  const initials =
    parts.length >= 2
      ? (parts[0]!.charAt(0) + parts[1]!.charAt(0)).toUpperCase()
      : local.slice(0, 2).toUpperCase();

  // If the derived name is a single word that matches (or is contained
  // in) the role label (case-insensitive), show the full email instead
  // so the two lines don't say the same thing.
  const roleLower = roleLabel.toLowerCase();
  const name =
    parts.length <= 1 && derived && roleLower.includes(derived.toLowerCase())
      ? email
      : derived || local;

  return { name: name || local, initials: initials || '??' };
}

export interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
  isLargeScreen: boolean;
  className?: string;
}

export const Sidebar: React.FC<SidebarProps> = ({
  isOpen,
  onToggle,
  isLargeScreen,
  className = '',
}) => {
  const { user } = useAuth();
  const location = useLocation();
  const [balance, setBalance] = useState<number | null>(null);

  // Fetch balance for company users to determine if Generate button should be enabled
  const userId = user?.id;
  const companyId = user?.companyId;

  useEffect(() => {
    if (!userId || !companyId) {
      setBalance(null);
      return;
    }

    let mounted = true;
    const controller = new AbortController();

    async function fetchBalance() {
      try {
        const response = await billingApi.getBalance(controller.signal);
        if (mounted && response.success) {
          setBalance(response.balance);
        }
      } catch {
        // Silently fail - button will remain enabled if we can't fetch balance
      }
    }

    fetchBalance();

    // Refresh balance every minute
    const BALANCE_REFRESH_INTERVAL_MS = 60 * 1000;
    const intervalId = window.setInterval(fetchBalance, BALANCE_REFRESH_INTERVAL_MS);

    return () => {
      mounted = false;
      controller.abort();
      window.clearInterval(intervalId);
    };
  }, [userId, companyId]);

  if (!user) {
    return null;
  }

  // Filter nav items based on user role
  const visibleItems = NAV_ITEMS.filter((item) => item.roles.includes(user.role));

  // Admins cannot generate; company users need credits
  const isAdmin = user.role === 'SUPER_ADMIN' || user.role === 'ADMIN';
  const canGenerate = !isAdmin && balance !== null && balance > 0;

  // Collapsed width for desktop
  const collapsedWidth = '64px';
  const expandedWidth = '260px';

  return (
    <>
      {/* Mobile overlay - only show on mobile when open */}
      {!isLargeScreen && isOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(15, 15, 16, 0.45)',
            backdropFilter: 'blur(2px)',
            WebkitBackdropFilter: 'blur(2px)',
            zIndex: 40,
          }}
          onClick={onToggle}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={className}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          bottom: 0,
          width: isLargeScreen ? (isOpen ? expandedWidth : collapsedWidth) : expandedWidth,
          backgroundColor: 'var(--bg-elevated)',
          borderRight: '1px solid var(--line)',
          zIndex: 50,
          transform: isLargeScreen ? 'translateX(0)' : (isOpen ? 'translateX(0)' : 'translateX(-100%)'),
          transition: 'transform var(--transition-base), width var(--transition-base)',
          overflowX: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Logo area */}
        <div
          style={{
            height: '60px',
            display: 'flex',
            alignItems: 'center',
            padding: isLargeScreen && !isOpen ? '0 var(--space-2)' : '0 var(--space-5)',
            borderBottom: '1px solid var(--line)',
            justifyContent: isLargeScreen && !isOpen ? 'center' : 'flex-start',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                minWidth: '32px',
                width: '32px',
                height: '32px',
                backgroundColor: 'var(--ink)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--bg)',
                fontFamily: 'var(--font-body)',
                fontWeight: 700,
                fontSize: '0.95rem',
                letterSpacing: '-0.03em',
                borderRadius: 'var(--radius-sm)',
                flexShrink: 0,
                position: 'relative',
              }}
            >
              W
              <span
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  inset: 'auto 3px 3px auto',
                  width: 5,
                  height: 5,
                  borderRadius: '50%',
                  background: 'var(--accent)',
                }}
              />
            </div>
            {(isOpen || !isLargeScreen) && (
              <div style={{ lineHeight: 1.1, whiteSpace: 'nowrap' }}>
                <div
                  style={{
                    fontFamily: 'var(--font-body)',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    letterSpacing: '-0.02em',
                    color: 'var(--ink)',
                  }}
                >
                  WebGen
                </div>
                <div
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: '0.62rem',
                    color: 'var(--ink-muted)',
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    marginTop: 2,
                  }}
                >
                  v2.0 · {BUILD_TAG}
                </div>
              </div>
            )}
          </div>

          {/* Mobile close button */}
          {!isLargeScreen && isOpen && (
            <button
              type="button"
              onClick={onToggle}
              style={{
                marginLeft: 'auto',
                width: 32,
                height: 32,
                padding: 0,
                backgroundColor: 'transparent',
                border: '1px solid var(--line)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--ink-soft)',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              aria-label="Close sidebar"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>

        {/* Navigation — grouped into WORKSPACE and YOU sections */}
        <nav
          style={{
            padding: isLargeScreen && !isOpen ? 'var(--space-4) var(--space-2)' : 'var(--space-5) var(--space-3)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-4)',
            flex: 1,
            overflowY: 'auto',
          }}
        >
          {(['workspace', 'team'] as const).map((groupKey) => {
            const groupItems = visibleItems.filter(
              (item) => sidebarGroup(item.path, item.label) === groupKey,
            );
            if (groupItems.length === 0) return null;
            return (
              <div key={groupKey} style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                {(isOpen || !isLargeScreen) && (
                  <div
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: '0.62rem',
                      fontWeight: 500,
                      letterSpacing: '0.14em',
                      color: 'var(--ink-muted)',
                      textTransform: 'uppercase',
                      padding: `0 var(--space-3) var(--space-2)`,
                    }}
                  >
                    {groupKey}
                  </div>
                )}
                {groupItems.map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      onClick={() => !isLargeScreen && onToggle()}
                      title={isLargeScreen && !isOpen ? item.label : undefined}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-3)',
                        padding: 'var(--space-3)',
                        backgroundColor: isActive ? 'var(--ink)' : 'transparent',
                        color: isActive ? 'var(--bg)' : 'var(--ink-soft)',
                        textDecoration: 'none',
                        fontFamily: 'var(--font-body)',
                        fontSize: 'var(--text-sm)',
                        fontWeight: 500,
                        letterSpacing: '-0.005em',
                        border: 'none',
                        borderRadius: 'var(--radius-md)',
                        transition: 'background-color var(--transition-fast), color var(--transition-fast)',
                        justifyContent: isLargeScreen && !isOpen ? 'center' : 'flex-start',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        position: 'relative',
                      }}
                      onMouseEnter={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.backgroundColor = 'var(--bg-sunk)';
                          e.currentTarget.style.color = 'var(--ink)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = 'var(--ink-soft)';
                        }
                      }}
                    >
                      {isActive && (
                        <span
                          aria-hidden="true"
                          style={{
                            position: 'absolute',
                            left: 4,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: 3,
                            height: 18,
                            background: 'var(--accent)',
                            borderRadius: '0 2px 2px 0',
                          }}
                        />
                      )}
                      <span
                        style={{
                          flexShrink: 0,
                          display: 'inline-flex',
                          width: 18,
                          height: 18,
                          alignItems: 'center',
                          justifyContent: 'center',
                          opacity: isActive ? 1 : 0.85,
                        }}
                      >
                        {item.icon}
                      </span>
                      {(isOpen || !isLargeScreen) && item.label}
                    </NavLink>
                  );
                })}
              </div>
            );
          })}
        </nav>

        {/* Bottom section — credit tile, system status, user row, toggle, generate */}
        <div
          style={{
            borderTop: '1px solid var(--line)',
            padding: isLargeScreen && !isOpen ? 'var(--space-3) var(--space-2)' : 'var(--space-4)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-3)',
          }}
        >
          {/* Credit balance tile — warm gradient over bg-sunk, matches ref.
              Only rendered for users with a company balance (admins get null). */}
          {balance !== null && (isOpen || !isLargeScreen) && (
            <div
              style={{
                padding: 'var(--space-3) var(--space-4)',
                background:
                  'linear-gradient(135deg, var(--bg-sunk), color-mix(in srgb, var(--accent) 12%, var(--bg)))',
                border: '1px solid var(--line)',
                borderRadius: 'var(--radius-md)',
              }}
            >
              <div
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: '0.62rem',
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: 'var(--ink-muted)',
                }}
              >
                Credits
              </div>
              <div
                style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '1.75rem',
                  fontWeight: 600,
                  letterSpacing: '-0.04em',
                  lineHeight: 1.05,
                  color: 'var(--ink)',
                  fontVariantNumeric: 'tabular-nums',
                  marginTop: 2,
                }}
              >
                {balance.toLocaleString()}
              </div>
              <div
                style={{
                  fontSize: 'var(--text-xs)',
                  color: 'var(--ink-muted)',
                  marginTop: 4,
                }}
              >
                ~{balance.toLocaleString()} generations available
              </div>
            </div>
          )}
          {/* Collapsed credit chip — just a small pill when sidebar is closed */}
          {balance !== null && isLargeScreen && !isOpen && (
            <div
              title={`${balance.toLocaleString()} credits`}
              style={{
                alignSelf: 'center',
                padding: '6px 8px',
                background: 'var(--ink)',
                color: 'white',
                borderRadius: 'var(--radius-sm)',
                fontFamily: 'var(--font-mono)',
                fontSize: '0.6rem',
                fontWeight: 600,
                letterSpacing: '0.02em',
              }}
            >
              {balance >= 1000 ? `${(balance / 1000).toFixed(1)}k` : balance}
            </div>
          )}

          {/* System status pill — mirrors the status indicator on the dark panels */}
          {(isOpen || !isLargeScreen) && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                fontFamily: 'var(--font-mono)',
                fontSize: '0.6rem',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: 'var(--ink-muted)',
                padding: '0 var(--space-1)',
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: '#16a34a',
                  boxShadow: '0 0 6px rgba(22, 163, 74, 0.6)',
                }}
              />
              All systems operational
            </div>
          )}

          {/* User identity row */}
          {(isOpen || !isLargeScreen) && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-3)',
                padding: 'var(--space-2) var(--space-1)',
                minWidth: 0,
              }}
            >
              {(() => {
                const roleLabel = ROLE_LABELS[user.role];
                const { name, initials } = deriveDisplayName(user.email, roleLabel);
                return (
                  <>
                    <div
                      aria-hidden="true"
                      style={{
                        flexShrink: 0,
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, var(--accent), #b02a0a)',
                        color: 'white',
                        display: 'grid',
                        placeItems: 'center',
                        fontFamily: 'var(--font-body)',
                        fontWeight: 600,
                        fontSize: 'var(--text-sm)',
                        letterSpacing: '0.02em',
                      }}
                    >
                      {initials}
                    </div>
                    <div style={{ minWidth: 0, flex: 1, lineHeight: 1.2 }}>
                      <div
                        title={user.email}
                        style={{
                          fontFamily: 'var(--font-body)',
                          fontSize: 'var(--text-sm)',
                          fontWeight: 500,
                          color: 'var(--ink)',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {name}
                      </div>
                      <div
                        style={{
                          fontSize: 'var(--text-xs)',
                          color: 'var(--ink-muted)',
                          marginTop: 1,
                        }}
                      >
                        {roleLabel}
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          )}

          {/* Desktop collapse/expand toggle */}
          {isLargeScreen && (
            <button
              type="button"
              onClick={onToggle}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: isOpen ? 'flex-start' : 'center',
                gap: 'var(--space-3)',
                padding: isOpen ? 'var(--space-2) var(--space-3)' : 'var(--space-2)',
                backgroundColor: 'transparent',
                border: '1px solid var(--line)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--ink-muted)',
                cursor: 'pointer',
                fontFamily: 'var(--font-mono)',
                fontSize: '0.7rem',
                fontWeight: 500,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                transition: 'background-color var(--transition-fast), color var(--transition-fast), border-color var(--transition-fast)',
                width: '100%',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--bg-sunk)';
                e.currentTarget.style.color = 'var(--ink)';
                e.currentTarget.style.borderColor = 'var(--line-strong)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = 'var(--ink-muted)';
                e.currentTarget.style.borderColor = 'var(--line)';
              }}
              title={isOpen ? 'Collapse sidebar' : 'Expand sidebar'}
              aria-label={isOpen ? 'Collapse sidebar' : 'Expand sidebar'}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{
                  transform: isOpen ? 'rotate(0deg)' : 'rotate(180deg)',
                  transition: 'transform var(--transition-base)',
                  flexShrink: 0,
                }}
              >
                <polyline points="11 17 6 12 11 7" />
                <polyline points="18 17 13 12 18 7" />
              </svg>
              {isOpen && 'Collapse'}
            </button>
          )}

          {/* Generate Website link/button */}
          {canGenerate ? (
            <NavLink
              to="/"
              onClick={() => !isLargeScreen && onToggle()}
              title={isLargeScreen && !isOpen ? 'Generate Website' : undefined}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 'var(--space-2)',
                padding: 'var(--space-3) var(--space-4)',
                backgroundColor: 'var(--accent)',
                color: 'white',
                textDecoration: 'none',
                fontFamily: 'var(--font-body)',
                fontSize: 'var(--text-sm)',
                fontWeight: 600,
                letterSpacing: '-0.01em',
                border: '1px solid var(--accent)',
                borderRadius: 'var(--radius-md)',
                boxShadow: '0 4px 12px rgba(200, 65, 26, 0.25)',
                transition: 'transform var(--transition-fast), box-shadow var(--transition-fast)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 8px 20px rgba(200, 65, 26, 0.35)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(200, 65, 26, 0.25)';
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.25"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ flexShrink: 0 }}
              >
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              {(isOpen || !isLargeScreen) && 'New Website'}
            </NavLink>
          ) : (
            <div
              title={isAdmin
                ? 'Admin accounts cannot generate websites'
                : isLargeScreen && !isOpen ? 'No credits available' : 'No credits available — contact your manager to add credits'}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 'var(--space-2)',
                padding: 'var(--space-3) var(--space-4)',
                backgroundColor: 'var(--bg-sunk)',
                color: 'var(--ink-faint)',
                fontFamily: 'var(--font-body)',
                fontSize: 'var(--text-sm)',
                fontWeight: 500,
                letterSpacing: '-0.005em',
                border: '1px dashed var(--line-strong)',
                borderRadius: 'var(--radius-md)',
                cursor: 'not-allowed',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ flexShrink: 0 }}
              >
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              {(isOpen || !isLargeScreen) && 'New Website'}
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
